import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import apiService from '../services/api';
import { TaskListItem, TaskMetaResponse } from '../types/task';
import TaskFormDialog from './TaskFormDialogV2';

type Props = {
  accountId: string;
};

const AccountTasksTab: React.FC<Props> = ({ accountId }) => {
  const [meta, setMeta] = useState<TaskMetaResponse | null>(null);
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [m, list] = await Promise.all([
        apiService.getTaskMeta(),
        apiService.listTasks({ page: 1, per_page: 100, account_id: accountId }),
      ]);
      setMeta(m);
      setTasks(list.tasks || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const statusLabel = (code?: string | null) => {
    const s = meta?.statuses?.find((x) => x.code === code);
    return s?.name || code || '';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Tasks
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New task
        </Button>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Due</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <Typography
                      component={RouterLink}
                      to={`/crm/tasks/${t.id}`}
                      sx={{ textDecoration: 'none', fontWeight: 600, color: 'primary.main' }}
                    >
                      {t.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={statusLabel(t.status_code)} variant="outlined" />
                  </TableCell>
                  <TableCell>{t.due_at ? new Date(t.due_at).toLocaleString() : '-'}</TableCell>
                </TableRow>
              ))}
              {tasks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="text.secondary">
                      No tasks for this account
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <TaskFormDialog
        open={createOpen}
        accountId={accountId}
        onClose={() => setCreateOpen(false)}
        onSaved={() => {
          setCreateOpen(false);
          fetchAll();
        }}
      />
    </Box>
  );
};

export default AccountTasksTab;
